import { useCallback, useEffect, useState } from "react";

/**
 * Light/dark preference.
 *
 * The page is themed entirely by CSS custom properties (see styles/main.css):
 * with no `data-theme` on <html>, `prefers-color-scheme` decides; setting the
 * attribute overrides it in either direction. index.html applies the stored
 * choice before first paint, so there is no flash of the wrong theme — this
 * module is only what keeps it in sync afterwards.
 */

export type ColorScheme = "light" | "dark";

export const SCHEME_STORAGE_KEY = "streamlover.theme";

const darkQuery = () => window.matchMedia("(prefers-color-scheme: dark)");

/** The visitor's explicit choice, if they have made one. */
export const storedScheme = (): ColorScheme | null => {
  try {
    const stored = localStorage.getItem(SCHEME_STORAGE_KEY);
    return stored === "light" || stored === "dark" ? stored : null;
  } catch {
    // Privacy modes can throw on access; treat it as "no choice made".
    return null;
  }
};

const systemScheme = (): ColorScheme =>
  darkQuery().matches ? "dark" : "light";

export const useColorScheme = () => {
  const [scheme, setScheme] = useState<ColorScheme>(
    () => storedScheme() ?? systemScheme(),
  );

  // Follow the OS while the visitor hasn't picked a side.
  useEffect(() => {
    const query = darkQuery();
    const onChange = (event: MediaQueryListEvent) => {
      if (!storedScheme()) {
        setScheme(event.matches ? "dark" : "light");
      }
    };
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = scheme;
  }, [scheme]);

  const chooseScheme = useCallback((next: ColorScheme) => {
    try {
      localStorage.setItem(SCHEME_STORAGE_KEY, next);
    } catch {
      // Non-fatal: the choice just won't survive a reload.
    }
    setScheme(next);
  }, []);

  return { scheme, chooseScheme };
};
